/**
 * buildBlogJsonLd — canonical JSON-LD builders for the blog surface.
 *
 * BLOG-STD-001: extracted from `BlogPostPage.jsx` so the React component (which
 * emits in-body BlogPosting JSON-LD) AND the prerender harness head builder
 * share ONE source — no schema drift. Adds BreadcrumbList + CollectionPage
 * builders for the index/category/tag prerendered pages.
 *
 * @module @puffin-core/marketing-shell/blog/buildBlogJsonLd
 */

// LIFE-SHORTLISTED-001 P1.5 — the URL join and the BreadcrumbList node are the shared schema.org
// builders (@puffin-core/seo), so the blog, the tenant storefront and the marketing graph emit one
// shape. BlogPosting / CollectionPage stay here: the blog is their only consumer. P12.4b — through
// the builders' own subpath: the package root would carry the whole seo graph into the blog's SSR
// bundle, the WP-fleet crawl map (which names tenant domains) included.
import {
  SCHEMA_CONTEXT,
  absoluteUrl,
  buildBreadcrumbJsonLd as buildBreadcrumbNode,
} from '@puffin-core/seo/schema-org';

/**
 * Build the BlogPosting JSON-LD payload from a post. Prefers the
 * upstream-authored `schemaArticleJson` (PublishPuffin's metadata step
 * produces this) and falls back to a canonical minimal BlogPosting.
 *
 * Returns `null` when the post lacks both a schemaArticleJson AND any field we
 * can use to synthesize one — better to emit nothing than a malformed schema.
 *
 * @param {object} post
 * @returns {object|null}
 */
export function buildBlogPostingJsonLd(post) {
  if (!post || (!post.title && !post.headline && !post.bodyHtml)) {
    return null;
  }
  if (post.schemaArticleJson && typeof post.schemaArticleJson === 'object') {
    return {
      '@context': SCHEMA_CONTEXT,
      '@type': 'BlogPosting',
      ...post.schemaArticleJson,
    };
  }
  const jsonLd = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BlogPosting',
    headline: post.title || post.headline,
    description: post.excerpt || undefined,
    datePublished: post.publishedAt || undefined,
    dateModified: post.modifiedAt || post.publishedAt || undefined,
    author: post.authorName ? { '@type': 'Person', name: post.authorName } : undefined,
    image: post.featuredImageUrl || undefined,
    articleBody: post.bodyHtml
      ? post.bodyHtml
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 1500)
      : undefined,
    wordCount: post.wordCount || undefined,
    keywords: Array.isArray(post.secondaryKeyphrases)
      ? post.secondaryKeyphrases.join(', ')
      : undefined,
  };
  for (const key of Object.keys(jsonLd)) {
    if (jsonLd[key] === undefined) delete jsonLd[key];
  }
  return jsonLd;
}

/**
 * Build a BreadcrumbList JSON-LD from an ordered crumb trail. Each crumb is
 * `{ name, path }` (path relative to baseUrl). Returns `null` when empty.
 *
 * @param {{ baseUrl: string, crumbs?: Array<{name: string, path: string}> }} args
 * @returns {object|null}
 */
export function buildBreadcrumbJsonLd({ baseUrl, crumbs }) {
  if (!Array.isArray(crumbs)) {
    return null;
  }
  return buildBreadcrumbNode({
    crumbs: crumbs.map((crumb) => ({ name: crumb.name, url: absoluteUrl(baseUrl, crumb.path) })),
  });
}

/**
 * Build a CollectionPage JSON-LD (with an ItemList of post URLs) for the blog
 * index / category / tag pages.
 *
 * @param {{ baseUrl: string, hrefPrefix: string, name: string, posts?: Array<{slug: string, title: string}> }} args
 * @returns {object}
 */
export function buildBlogCollectionJsonLd({ baseUrl, hrefPrefix, name, posts }) {
  const rows = Array.isArray(posts) ? posts : [];
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'CollectionPage',
    name,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: rows.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: absoluteUrl(baseUrl, `${hrefPrefix}/${post.slug}`),
        name: post.title,
      })),
    },
  };
}
